import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useTheme,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

const TermsAndConditions = ({ open, onClose, onAccept }) => {
  const theme = useTheme();

  const terms = [
    {
      title: "Privacy & Anonymity",
      content: "We prioritize your privacy. No personal information is collected or stored. Chat sessions are completely anonymous.",
      color: theme.palette.primary.main
    },
    {
      title: "Code of Conduct",
      content: "Be respectful to others. Harassment, hate speech, and inappropriate content are strictly prohibited.",
      color: theme.palette.secondary.main
    },
    {
      title: "Data Usage",
      content: "Messages are not stored on our servers. Once you leave a chat, the conversation is permanently deleted.",
      color: theme.palette.info.main
    },
    {
      title: "User Safety",
      content: "You can block and report users who violate our terms. We maintain the right to ban users who misuse the platform.",
      color: theme.palette.success.main
    }
  ];

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="sm"
          fullWidth
          PaperComponent={motion.div}
          PaperProps={{
            initial: { opacity: 0, y: 20 },
            animate: { opacity: 1, y: 0 },
            exit: { opacity: 0, y: -20 },
            transition: { duration: 0.3 },
            style: {
              borderRadius: 16,
              background: theme.palette.background.paper,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }
          }}
        >
          <DialogTitle>
            <Typography 
              variant="h4" 
              component="div"
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                fontSize: { xs: '1.75rem', sm: '2.25rem' },
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textAlign: 'center',
                mb: 2
              }}
            >
              Terms and Conditions
            </Typography>
          </DialogTitle>
          
          <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
              {terms.map((term, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Box
                    sx={{
                      p: { xs: 2, sm: 3 },
                      borderRadius: 2,
                      background: theme.palette.grey[100],
                      border: `1px solid ${theme.palette.grey[200]}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        borderColor: term.color,
                      }
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      gutterBottom
                      sx={{ 
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 600,
                        color: term.color,
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                      }}
                    >
                      {term.title}
                    </Typography>
                    <Typography 
                      variant="body1"
                      sx={{ 
                        color: theme.palette.text.secondary,
                        fontFamily: "'Inter', sans-serif",
                        lineHeight: 1.6,
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}
                    >
                      {term.content}
                    </Typography>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: { xs: 1, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' } }}>
            <motion.div whileHover={{ scale: 1.02 }} style={{ width: '100%' }}>
              <Button 
                onClick={onClose}
                fullWidth
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 500,
                  borderColor: theme.palette.grey[300],
                  color: theme.palette.text.primary,
                  '&:hover': {
                    borderColor: theme.palette.grey[400],
                    backgroundColor: theme.palette.grey[100],
                  }
                }}
              >
                Decline
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.02 }} style={{ width: '100%' }}>
              <Button
                onClick={onAccept}
                fullWidth
                variant="contained"
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 500,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  }
                }}
              >
                Accept
              </Button>
            </motion.div>
          </DialogActions>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default TermsAndConditions; 